import { useMemo, useState } from 'react';
import { AlertTriangle, CalendarRange, Check, Pencil, Plus, Scissors, X } from 'lucide-react';
import type { BudgetPeriodOverview } from '@/api/types';
import { useBudgetPeriods, usePeriodPlan, useSummary } from '@/api/hooks';
import { formatDate, formatMoney, formatMoneyShort, formatMonthRange } from '@/lib/format';
import { UNGROUPED_COLOR } from '@/lib/palette';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { GroupBadge, StatusBadge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { StatTile } from '@/components/charts/StatTile';
import { BudgetMeter } from '@/components/charts/BudgetMeter';
import { PeriodDialog } from '@/components/PeriodDialog';
import { InvestmentDialog } from '@/components/InvestmentDialog';
import { PageHeader, QueryState } from '@/components/PageHeader';
import { useGroups } from '@/api/hooks';
import { useIsAdmin } from '@/auth/account';

/** The year to pre-fill for a new period: the one after the last period, else this one. */
function nextFreeYear(periods: BudgetPeriodOverview[]): number {
  if (periods.length === 0) return new Date().getFullYear();
  const lastEnd = periods[periods.length - 1]!.endDate;
  return Number(lastEnd.slice(0, 4)) + 1;
}

function PeriodCard({
  period,
  currency,
  selected,
  onSelect,
  onEdit,
}: {
  period: BudgetPeriodOverview;
  currency: string;
  selected: boolean;
  onSelect: () => void;
  /** Omitted for non-admins – only they may change a budget. */
  onEdit?: () => void;
}) {
  const over = period.remaining < 0;
  return (
    <Card
      className={[
        'flex flex-col gap-3 px-5 py-4 transition-colors',
        selected ? 'ring-2 ring-[var(--accent)]' : '',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <button type="button" onClick={onSelect} className="min-w-0 text-left">
          <h2 className="text-base font-semibold tracking-tight">{period.name}</h2>
          <p className="num mt-0.5 text-xs text-[var(--ink-3)]">
            {formatDate(period.startDate)} – {formatDate(period.endDate)}
          </p>
        </button>
        {onEdit ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label={`${period.name} bearbeiten`}
            onClick={onEdit}
          >
            <Pencil size={15} />
          </Button>
        ) : null}
      </div>

      {period.amount > 0 ? (
        <BudgetMeter planned={period.planned} budget={period.amount} currency={currency} />
      ) : (
        <p className="text-xs text-[var(--ink-3)]">Kein Budget gesetzt.</p>
      )}

      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 border-t border-[var(--border)] pt-3 text-xs">
        <dt className="text-[var(--ink-3)]">Verplant</dt>
        <dd className="num text-right font-medium">
          {formatMoneyShort(period.planned, currency)}
        </dd>
        <dt className="text-[var(--ink-3)]">{over ? 'Darüber' : 'Frei'}</dt>
        <dd
          className={`num text-right font-medium ${
            over ? 'text-[var(--critical)]' : 'text-[var(--good)]'
          }`}
        >
          {formatMoneyShort(Math.abs(period.remaining), currency)}
        </dd>
        <dt className="text-[var(--ink-3)]">Passt ins Budget</dt>
        <dd className="num text-right font-medium">
          {period.fittingCount} von {period.investmentCount}
        </dd>
      </dl>

      {period.note ? (
        <p className="text-xs text-[var(--ink-3)] italic">{period.note}</p>
      ) : null}

      {period.exceedingCount > 0 ? (
        <p className="flex items-center gap-1.5 text-xs text-[var(--critical)]">
          <AlertTriangle size={13} aria-hidden="true" />
          {period.exceedingCount} Investition{period.exceedingCount === 1 ? '' : 'en'} passt nicht
          mehr
        </p>
      ) : null}

      <Button size="sm" variant={selected ? 'primary' : 'secondary'} onClick={onSelect}>
        {selected ? 'Plan unten' : 'Plan anzeigen'}
      </Button>
    </Card>
  );
}

export function Budget() {
  const periods = useBudgetPeriods();
  const summary = useSummary();
  const groups = useGroups();
  const isAdmin = useIsAdmin();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [periodDialog, setPeriodDialog] = useState<{
    open: boolean;
    period: BudgetPeriodOverview | null;
  }>({ open: false, period: null });
  const [investmentId, setInvestmentId] = useState<string | null>(null);

  const list = periods.data ?? [];
  const currency = summary.data?.currency ?? 'EUR';

  // Default to the period covering today, otherwise the first one.
  const today = new Date().toISOString().slice(0, 10);
  const activeId = useMemo(() => {
    if (selectedId && list.some((period) => period.id === selectedId)) return selectedId;
    const current = list.find(
      (period) => period.startDate <= today && period.endDate >= today,
    );
    return current?.id ?? list[0]?.id ?? null;
  }, [selectedId, list, today]);

  const plan = usePeriodPlan(activeId);
  const frame = summary.data?.budget;

  return (
    <>
      <PageHeader
        title="Budget"
        subtitle="Ein Budget je Zeitraum – und was davon in welchem Jahr Platz hat."
        actions={
          isAdmin ? (
            <Button
              variant="primary"
              onClick={() => setPeriodDialog({ open: true, period: null })}
            >
              <Plus size={15} />
              Neue Periode
            </Button>
          ) : undefined
        }
      />

      <QueryState
        isLoading={periods.isLoading || summary.isLoading}
        error={((periods.error ?? summary.error) as Error | null) ?? null}
      >
        {list.length === 0 ? (
          <Card>
            <EmptyState
              icon={<CalendarRange size={24} />}
              title="Noch kein Budget je Zeitraum"
              description={
                isAdmin
                  ? 'Lege eine Periode an – üblicherweise ein Kalenderjahr – und verteile die Vorhaben darauf.'
                  : 'Ein Admin kann hier Budgets je Zeitraum anlegen.'
              }
              action={
                isAdmin ? (
                  <Button
                    variant="primary"
                    onClick={() => setPeriodDialog({ open: true, period: null })}
                  >
                    <Plus size={15} />
                    Erste Periode anlegen
                  </Button>
                ) : undefined
              }
            />
          </Card>
        ) : (
          <div className="flex flex-col gap-5">
            {frame ? (
              <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <StatTile
                  label="Budgetrahmen"
                  value={formatMoneyShort(frame.total, currency)}
                  hint={`Summe aus ${list.length} Periode${list.length === 1 ? '' : 'n'}`}
                />
                <StatTile
                  label="Geplante Gesamtkosten"
                  value={formatMoneyShort(summary.data!.totals.effective, currency)}
                  hint={`${summary.data!.totals.investmentCount} Investitionen`}
                />
                <StatTile
                  label={frame.remaining < 0 ? 'Über dem Rahmen' : 'Im Rahmen frei'}
                  value={formatMoneyShort(Math.abs(frame.remaining), currency)}
                  tone={frame.remaining < 0 ? 'critical' : 'good'}
                  hint={
                    frame.remaining < 0
                      ? 'Rahmen erhöhen oder Vorhaben verschieben'
                      : 'Noch nicht verplant'
                  }
                />
                <StatTile
                  label="Außerhalb aller Perioden"
                  value={formatMoneyShort(frame.unbudgeted.total, currency)}
                  hint={`${frame.unbudgeted.investmentCount} terminiert, aber ohne Budget`}
                  tone={frame.unbudgeted.investmentCount > 0 ? 'critical' : 'neutral'}
                />
              </section>
            ) : null}

            <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {list.map((period) => (
                <PeriodCard
                  key={period.id}
                  period={period}
                  currency={currency}
                  selected={period.id === activeId}
                  onSelect={() => setSelectedId(period.id)}
                  onEdit={isAdmin ? () => setPeriodDialog({ open: true, period }) : undefined}
                />
              ))}
            </section>

            {plan.data ? (
              <Card>
                <CardHeader
                  title={`Was passt in ${plan.data.period.name}?`}
                  subtitle="In Prioritätsfolge aufaddiert – ab der Schnittlinie reicht das Budget nicht mehr"
                  action={
                    <span className="num text-sm font-semibold">
                      {formatMoneyShort(plan.data.fitting.total, currency)} von{' '}
                      {formatMoneyShort(plan.data.period.amount, currency)}
                    </span>
                  }
                />
                {plan.data.items.length === 0 ? (
                  <EmptyState
                    icon={<CalendarRange size={20} />}
                    title="Nichts in diesem Zeitraum"
                    description="Terminiere Investitionen in diesen Zeitraum, damit sie hier auftauchen."
                  />
                ) : (
                  <ul className="flex flex-col px-2 pb-3">
                    {plan.data.items.map((item, index) => {
                      const previous = plan.data!.items[index - 1];
                      const isCut = !item.fits && (index === 0 || previous?.fits === true);
                      return (
                        <li key={item.id}>
                          {isCut ? (
                            <div
                              aria-hidden="true"
                              className="flex items-center gap-2 px-3 py-2 text-[11px] font-medium text-[var(--critical)]"
                            >
                              <Scissors size={12} className="shrink-0" />
                              <span className="shrink-0">Budget ausgeschöpft</span>
                              <span className="h-px flex-1 bg-[var(--critical)] opacity-40" />
                            </div>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => setInvestmentId(item.id)}
                            className={[
                              'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                              'hover:bg-[var(--surface-2)]',
                              item.fits ? '' : 'opacity-65',
                            ].join(' ')}
                          >
                            <span
                              aria-hidden="true"
                              className={[
                                'grid h-6 w-6 shrink-0 place-items-center rounded-full',
                                item.fits
                                  ? 'bg-[var(--good)]/15 text-[var(--good)]'
                                  : 'bg-[var(--critical)]/15 text-[var(--critical)]',
                              ].join(' ')}
                            >
                              {item.fits ? <Check size={13} /> : <X size={13} />}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium">{item.title}</p>
                              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                <GroupBadge
                                  name={item.groupName ?? 'Ohne Gruppe'}
                                  color={item.groupColor ?? UNGROUPED_COLOR}
                                />
                                <StatusBadge status={item.status} />
                                <span className="num text-[11px] text-[var(--ink-3)]">
                                  {formatMonthRange(item.plannedFrom, item.plannedTo)}
                                </span>
                              </div>
                            </div>
                            <div className="shrink-0 text-right">
                              <p className="num text-sm font-medium">
                                {formatMoney(item.effectiveCost, currency)}
                              </p>
                              <p className="num text-[11px] text-[var(--ink-3)]">
                                kumuliert {formatMoneyShort(item.cumulative, currency)}
                              </p>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <p className="border-t border-[var(--border)] px-5 py-3 text-xs text-[var(--ink-3)]">
                  Die Reihenfolge kommt aus der Prioritätenliste. Wer etwas vorziehen will,
                  sortiert es dort nach oben – oder terminiert es in eine andere Periode.
                </p>
              </Card>
            ) : null}
          </div>
        )}
      </QueryState>

      <PeriodDialog
        open={periodDialog.open}
        period={periodDialog.period}
        suggestedYear={nextFreeYear(list)}
        onClose={() => setPeriodDialog({ open: false, period: null })}
      />
      <InvestmentDialog
        open={investmentId !== null}
        investmentId={investmentId}
        groups={groups.data ?? []}
        currency={currency}
        onClose={() => setInvestmentId(null)}
      />
    </>
  );
}
